import { Component, Inject, OnInit } from "@angular/core";
import {
  AbstractControl,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { NgxSpinnerService } from "ngx-spinner";
import { EnumHelper } from "src/app/core/helpers/enumHelper";
import {
  LicenseServerTypes,
  NameValueObject,
} from "src/app/core/models/globals";
import { NewLicenseServerInterface } from "src/app/core/models/licenseServer";
import { LicenseServerService } from "src/app/core/services/license-server.service";

@Component({
  selector: "app-add-license-server",
  templateUrl: "./add-license-server.component.html",
  styleUrls: ["./add-license-server.component.scss"],
})
export class AddLicenseServerComponent implements OnInit {
  licenseTypes: NameValueObject[] =
    EnumHelper.getTextAndValue(LicenseServerTypes);
  addLicenseServerForm: UntypedFormGroup = new UntypedFormGroup({
    licenseName: new UntypedFormControl(null, [
      Validators.required,
      Validators.pattern("^[A-Za-z0-9._-]*$"),
    ]),
    licenseType: new UntypedFormControl(this.licenseTypes[0].value, [
      Validators.required,
    ]),
    licenseHost: new UntypedFormControl(null, [
      Validators.required,
      Validators.pattern("^[A-Za-z0-9._-]*$"),
    ]),
    licensePort: new UntypedFormControl(null, [
      Validators.required,
      Validators.min(1),
      Validators.pattern("^[0-9]*$"),
    ]),
  });

  constructor(
    public dialog: MatDialogRef<AddLicenseServerComponent>,
    private _licenseServerService: LicenseServerService,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      licenseObj?: NewLicenseServerInterface;
    },
    private snackBar: MatSnackBar,
    private _spinner: NgxSpinnerService
  ) {}

  ngOnInit(): void {
    if (this.data?.licenseObj) {
      this.updateValuesTOForm(this.data.licenseObj);
    }
  }

  updateValuesTOForm(values: NewLicenseServerInterface) {
    this.addLicenseServerForm.patchValue({
      licenseName: values.name,
      licenseHost: values.host,
      licensePort: values.port,
    });
    this.addLicenseServerForm.controls["licenseType"].setValue(
      this.licenseTypes[values.type].value
    );
    this.addLicenseServerForm.updateValueAndValidity();
  }

  onClose() {
    this.dialog.close(false);
  }

  submitForm() {
    if (!this.addLicenseServerForm.valid) {
      this.addLicenseServerForm.markAllAsTouched();
      return;
    }
    if (!this.addLicenseServerForm.dirty) {
      this.openSnackBar("No changes were made", 5000);
      return;
    }
    this._spinner.show("inProgressAddUpdate");
    let licenseObj: NewLicenseServerInterface = {
      name: this.addLicenseServerForm.value.licenseName,
      type: +this.addLicenseServerForm.value.licenseType,
      host: this.addLicenseServerForm.value.licenseHost,
      port: +this.addLicenseServerForm.value.licensePort,
    };
    if (this.data?.licenseObj) {
      this._licenseServerService
        .updateLicenseServer(this.data.licenseObj.name, licenseObj)
        .subscribe({
          next: (data: any) => {
            this.openSnackBar("License server updated successfully", 5000);
          },
          error: (err: any) => {
            this.openSnackBar(
              "Error updating license server: " + err.message,
              5000
            );
          },
        });
    } else {
      this._licenseServerService.addLicenseServer(licenseObj).subscribe({
        next: (data: any) => {
          this.openSnackBar("License server added successfully", 5000);
        },
        error: (err) => {
          this.openSnackBar(
            "Error adding license server: " + err.message,
            5000
          );
        },
      });
    }
  }

  openSnackBar(message: string, duration: number) {
    this.snackBar.open(message, "Ok", {
      duration: duration,
    });
    this._spinner.hide("inProgressAddUpdate");
    this.dialog.close(true);
  }

  checkMinMaxOfPort(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value) return null;
      if (+value < 999 || +value > 9999) {
        return { minMaxError: true };
      }
      return null;
    };
  }
}
